import React, { useEffect, useState, useCallback, useMemo } from "react";
import { api, formatApiError, resolveImg } from "../../lib/api";
import { toast } from "sonner";
import { Trash2, Copy, FileText, Image as ImageIcon, Search } from "lucide-react";

export default function AdminMedia() {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | unused | used
  const [kindFilter, setKindFilter] = useState("all"); // all | image | pdf
  const [query, setQuery] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/media");
      setFiles(data.files || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed to load media");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    return files.filter((f) => {
      if (filter === "unused" && f.used) return false;
      if (filter === "used" && !f.used) return false;
      if (kindFilter !== "all" && f.kind !== kindFilter) return false;
      if (query && !f.name.toLowerCase().includes(query.toLowerCase())) return false;
      return true;
    });
  }, [files, filter, kindFilter, query]);

  const counts = useMemo(() => ({
    all: files.length,
    unused: files.filter((f) => !f.used).length,
    used: files.filter((f) => f.used).length,
  }), [files]);

  const copyUrl = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("URL copied");
    } catch {
      toast.error("Could not copy");
    }
  };

  const deleteFile = async (name) => {
    if (!confirm(`Delete ${name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/media/${encodeURIComponent(name)}`);
      toast.success("Deleted");
      setFiles((prev) => prev.filter((f) => f.name !== name));
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Delete failed");
    }
  };

  const fmtSize = (b) => {
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div data-testid="admin-media-page">
      <div className="flex items-baseline justify-between mb-2">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-electric">Library</div>
          <h1 className="font-display font-black text-3xl tracking-tight">Media</h1>
        </div>
        <div className="text-xs font-mono text-zinc-500">
          {counts.all} files · <span className="text-amber-600">{counts.unused} unused</span> · {counts.used} attached
        </div>
      </div>
      <p className="text-sm text-zinc-600 max-w-2xl mb-6">
        All images and PDFs uploaded through the admin live here. <strong>Unused</strong> files
        aren't linked to any product, category or logo — typically these are originals you uploaded
        before that got disconnected. Click <em>Copy URL</em> and paste it into a product's image
        field, or delete the ones you don't need.
      </p>

      <div className="flex flex-wrap gap-2 mb-4 items-center">
        {[
          { v: "all", l: `All (${counts.all})` },
          { v: "unused", l: `Unused (${counts.unused})` },
          { v: "used", l: `Attached (${counts.used})` },
        ].map((opt) => (
          <button
            key={opt.v}
            data-testid={`media-filter-${opt.v}`}
            onClick={() => setFilter(opt.v)}
            className={`h-8 px-3 text-[11px] font-mono uppercase tracking-[0.2em] border transition-colors ${
              filter === opt.v
                ? "bg-ink text-white border-ink"
                : "border-zinc-300 hover:border-ink"
            }`}
          >
            {opt.l}
          </button>
        ))}
        <span className="w-px h-6 bg-zinc-200 mx-2" />
        {["all", "image", "pdf"].map((k) => (
          <button
            key={k}
            data-testid={`media-kind-${k}`}
            onClick={() => setKindFilter(k)}
            className={`h-8 px-3 text-[11px] font-mono uppercase tracking-[0.2em] border transition-colors ${
              kindFilter === k
                ? "bg-ink text-white border-ink"
                : "border-zinc-300 hover:border-ink"
            }`}
          >
            {k}
          </button>
        ))}
        <div className="ml-auto relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input
            data-testid="media-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Filter by name…"
            className="h-8 pl-8 pr-3 text-xs border border-zinc-300 focus:outline-none focus:border-ink w-56"
          />
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-zinc-500">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="text-sm text-zinc-500 border border-dashed border-zinc-300 p-12 text-center">
          No files match the current filter.
        </div>
      ) : (
        <div
          data-testid="admin-media-grid"
          className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-3"
        >
          {filtered.map((f) => (
            <div
              key={f.name}
              data-testid={`media-card-${f.name}`}
              className="border border-zinc-200 bg-white flex flex-col group hover:border-ink transition-colors"
            >
              <div className="aspect-square bg-zinc-50 flex items-center justify-center overflow-hidden relative">
                {f.kind === "image" ? (
                  <img
                    src={resolveImg(f.url)}
                    alt={f.name}
                    loading="lazy"
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <div className="flex flex-col items-center gap-2 text-zinc-500">
                    <FileText size={40} />
                    <span className="text-[11px] font-mono uppercase">{f.ext}</span>
                  </div>
                )}
                {!f.used && (
                  <span className="absolute top-2 left-2 px-2 py-0.5 bg-amber-500 text-white text-[10px] font-mono uppercase tracking-wider">
                    Unused
                  </span>
                )}
              </div>
              <div className="p-2 text-[11px] font-mono text-zinc-700 truncate" title={f.name}>
                {f.name}
              </div>
              <div className="px-2 pb-1 text-[10px] font-mono text-zinc-400 flex justify-between">
                <span>{fmtSize(f.size)}</span>
                <span className="uppercase">{f.kind}</span>
              </div>
              <div className="px-2 pb-2 flex gap-1">
                <button
                  data-testid={`media-copy-${f.name}`}
                  onClick={() => copyUrl(f.url)}
                  title="Copy URL to paste into a product"
                  className="flex-1 h-7 text-[10px] font-mono uppercase tracking-wider border border-zinc-300 hover:bg-ink hover:text-white hover:border-ink transition-colors flex items-center justify-center gap-1"
                >
                  <Copy size={11} /> Copy URL
                </button>
                {!f.used && (
                  <button
                    data-testid={`media-delete-${f.name}`}
                    onClick={() => deleteFile(f.name)}
                    title="Permanently delete"
                    className="h-7 px-2 text-[10px] font-mono uppercase border border-zinc-300 hover:bg-red-600 hover:text-white hover:border-red-600 transition-colors"
                  >
                    <Trash2 size={11} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
